'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { OutcomeDot } from '@/components/shared/outcome-indicator';
import { SelectField, type SelectFieldItem } from '@/components/ui/select-field';
import { outcomeHueToStepVar, useMarketOutcomeColors } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// ── Types ────────────────────────────────────────────────────────────────────

interface OutcomeOption {
  index: number;
  label: string;
  /** Optional outcome image URL from backend. */
  imageUrl?: string | undefined;
  /** NSFW moderation flag for the outcome image (spec 071). */
  imageNsfw?: boolean | undefined;
}
interface OutcomePillsProps {
  /** Parent market / proposal pubkey — seeds the outcome indicator's pattern lib. */
  marketPubkey: string;
  outcomes: OutcomeOption[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** Optional prefix label shown inside the button (e.g. "From", "To"). */
  prefixLabel?: string;
  /** When true, the trigger button renders without its own border. */
  borderless?: boolean;
  /** Pre-formatted balance strings keyed by outcome index, shown in dropdown rows. */
  outcomeBalances?: Readonly<Record<number, string>>;
  /** Show outcome images in indicators. Default: true. Set false for trade modal. */
  showImages?: boolean;
  /** Extra classes for the underlying Select trigger (e.g. surface bg override). */
  triggerClassName?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Outcome selector — built on Radix Select so it works inside SilkSheet on
 * mobile (Radix Popover gets its touch events swallowed by Silk's gesture
 * handlers; Radix Select portals to body and escapes that scope).
 *
 * Hidden when there are no outcomes; renders a static label when only one.
 */
export function OutcomePills(t0) {
  const $ = _c(41);
  const {
    marketPubkey,
    outcomes,
    selectedIndex,
    onSelect,
    prefixLabel,
    borderless: t1,
    outcomeBalances,
    showImages: t2,
    triggerClassName
  } = t0;
  const borderless = t1 === undefined ? false : t1;
  t2 === undefined ? true : t2;
  const t = useTranslations("trade");
  let t3;
  if ($[0] !== outcomes) {
    t3 = outcomes.filter(_temp);
    $[0] = outcomes;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const realOutcomes = t3;
  const colorClasses = useMarketOutcomeColors(marketPubkey, realOutcomes);
  let colorByIndex;
  if ($[2] !== colorClasses || $[3] !== realOutcomes) {
    colorByIndex = {};
    for (let i = 0; i < realOutcomes.length; i++) {
      const o_0 = realOutcomes[i];
      const cls = colorClasses[i];
      if (o_0 !== undefined && cls?.hue !== undefined) {
        colorByIndex[o_0.index] = outcomeHueToStepVar(cls.hue, 400);
      }
    }
    $[2] = colorClasses;
    $[3] = realOutcomes;
    $[4] = colorByIndex;
  } else {
    colorByIndex = $[4];
  }
  if (outcomes.length === 0) {
    return null;
  }
  const hasMultiple = outcomes.length > 1;
  let t4;
  if ($[5] !== outcomes || $[6] !== selectedIndex) {
    let t5;
    if ($[8] !== selectedIndex) {
      t5 = o_1 => o_1.index === selectedIndex;
      $[8] = selectedIndex;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = outcomes.find(t5);
    $[5] = outcomes;
    $[6] = selectedIndex;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selected = t4;
  if (!hasMultiple) {
    let t5;
    if ($[10] !== borderless || $[11] !== colorByIndex || $[12] !== outcomeBalances || $[13] !== prefixLabel || $[14] !== selected) {
      t5 = <SingleOutcomeSelection selected={selected} colorByIndex={colorByIndex} outcomeBalances={outcomeBalances} prefixLabel={prefixLabel} borderless={borderless} />;
      $[10] = borderless;
      $[11] = colorByIndex;
      $[12] = outcomeBalances;
      $[13] = prefixLabel;
      $[14] = selected;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  let t5;
  if ($[16] !== colorByIndex || $[17] !== outcomeBalances || $[18] !== outcomes) {
    let t6;
    if ($[20] !== colorByIndex || $[21] !== outcomeBalances) {
      t6 = outcome => {
        const isAllOption = outcome.index < 0;
        const dotStyle = colorByIndex[outcome.index] ?? "var(--color-foreground-tertiary)";
        const balance = outcomeBalances?.[outcome.index];
        return {
          value: outcome.index.toString(),
          label: <span className="flex w-full min-w-0 items-baseline gap-2">{!isAllOption && <OutcomeDot color={dotStyle} />}<span className="truncate">{outcome.label}</span>{balance !== undefined && <span className="ml-auto shrink-0 text-caption tabular-nums text-foreground-secondary">{balance}</span>}</span>
        };
      };
      $[20] = colorByIndex;
      $[21] = outcomeBalances;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t5 = outcomes.map(t6);
    $[16] = colorByIndex;
    $[17] = outcomeBalances;
    $[18] = outcomes;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const items = t5;
  let t6;
  if ($[23] !== selectedIndex) {
    t6 = selectedIndex.toString();
    $[23] = selectedIndex;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== onSelect) {
    t7 = v => {
      onSelect(Number(v));
    };
    $[25] = onSelect;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t) {
    t8 = t("select_outcome");
    $[27] = t;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== prefixLabel) {
    t9 = prefixLabel !== undefined && prefixLabel.length > 0 ? {
      label: prefixLabel
    } : {};
    $[29] = prefixLabel;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const t10 = borderless && "border-0 bg-transparent shadow-none";
  let t11;
  if ($[31] !== t10 || $[32] !== triggerClassName) {
    t11 = cn(t10, triggerClassName);
    $[31] = t10;
    $[32] = triggerClassName;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== items || $[35] !== t11 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t12 = <SelectField value={t6} onValueChange={t7} items={items} placeholder={t8} {...t9} triggerClassName={t11} />;
    $[34] = items;
    $[35] = t11;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}
function _temp(o) {
  return o.index >= 0;
}
function SingleOutcomeSelection(t0) {
  const $ = _c(22);
  const {
    selected,
    colorByIndex,
    outcomeBalances,
    prefixLabel,
    borderless
  } = t0;
  const t = useTranslations("trade");
  const isAllSelected = selected !== undefined && selected.index < 0;
  const selectedDotColor = selected !== undefined && !isAllSelected ? colorByIndex[selected.index] ?? "var(--color-foreground-tertiary)" : undefined;
  const selectedBalance = selected !== undefined ? outcomeBalances?.[selected.index] : undefined;
  const t1 = borderless ? "px-3 py-2" : "h-10 rounded-control border border-border-primary bg-background-secondary px-3";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex w-full items-center justify-between gap-2 text-body-lg", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== prefixLabel) {
    t3 = prefixLabel !== undefined && prefixLabel.length > 0 && <span className="shrink-0 text-caption font-normal text-foreground-secondary">{prefixLabel}</span>;
    $[2] = prefixLabel;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isAllSelected || $[5] !== selectedDotColor) {
    t4 = !isAllSelected && selectedDotColor !== undefined && <OutcomeDot color={selectedDotColor} />;
    $[4] = isAllSelected;
    $[5] = selectedDotColor;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== selected?.label || $[8] !== t) {
    t5 = selected?.label ?? t("select_outcome");
    $[7] = selected?.label;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="truncate">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-2 truncate">{t3}{t4}{t6}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== selectedBalance) {
    t8 = selectedBalance !== undefined && <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{selectedBalance}</span>;
    $[16] = selectedBalance;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t2 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className={t2}>{t7}{t8}</div>;
    $[18] = t2;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}